import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

const StatCard = ({ icon, label, value, sub, color, to }) => {
  const card = (
    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px', cursor: to ? 'pointer' : 'default' }}>
      <div className="card-body p-4">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div style={{ fontSize: '28px' }}>{icon}</div>
          {to && <i className="bi bi-arrow-right-short text-muted fs-5"></i>}
        </div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: color || '#2d1b69', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted2)', marginTop: '4px' }}>{label}</div>
        {sub && <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '4px' }}>{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{card}</Link> : card;
};

const AdminDashboard = () => {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/admin/dashboard')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title="Dashboard">
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
      </div>
    </AdminLayout>
  );

  const { stats, recentUsers, recentMessages, recentGifts, dailySignups } = data;

  const planColor = { free: '#6b7280', basic: '#6f42c1', family: '#059669' };
  const planLabel = { free: 'Free', basic: 'Basic', family: 'Family' };
  const statusColor = {
    open: 'danger', in_progress: 'warning', resolved: 'success', closed: 'secondary',
    pending: 'secondary', paid: 'info', generating: 'warning', delivered: 'success', failed: 'danger',
  };

  // Build sparkline-style bar chart for daily signups
  const maxDay = Math.max(...dailySignups.map(d => d.count), 1);

  return (
    <AdminLayout title="Dashboard">

      {/* Stat cards row 1 */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <StatCard icon="👥" label="Total Users" value={stats.totalUsers.toLocaleString()}
            sub={`+${stats.newUsersToday} today`} color="#2d1b69" to="/admin/users" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="💰" label="Est. MRR" value={`$${stats.mrr.toFixed(2)}`}
            sub={`${stats.plans.basic} Basic · ${stats.plans.family} Family`} color="#059669" to="/admin/revenue" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="📖" label="Stories This Month" value={stats.storiesThisMonth.toLocaleString()}
            sub={`${stats.totalStories.toLocaleString()} total`} color="#6f42c1" to="/admin/stories" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="🎫" label="Open Support" value={stats.openTickets}
            sub="open + in progress" color={stats.openTickets > 0 ? '#dc2626' : '#059669'} to="/admin/support" />
        </div>
      </div>

      {/* Stat cards row 2 */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <StatCard icon="🆓" label="Free Users" value={stats.plans.free || 0} color="#6b7280" to="/admin/users?plan=free" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="⭐" label="Basic Subscribers" value={stats.plans.basic || 0} color="#6f42c1" to="/admin/users?plan=basic" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="👑" label="Family Subscribers" value={stats.plans.family || 0} color="#059669" to="/admin/users?plan=family" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="🎁" label="Gift Orders (Month)" value={stats.giftOrdersThisMonth}
            color="#d97706" to="/admin/gifts" />
        </div>
      </div>

      <div className="row g-3">
        {/* Signups chart */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>New Signups — Last 30 Days</h6>
              <p className="text-muted small mb-3">{stats.newUsersThisMonth} this calendar month</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '80px' }}>
                {dailySignups.length === 0 ? (
                  <p className="text-muted small">No data yet</p>
                ) : dailySignups.map((d, i) => (
                  <div
                    key={i}
                    title={`${d._id}: ${d.count} signup${d.count !== 1 ? 's' : ''}`}
                    style={{
                      flex: 1,
                      height: `${Math.max(4, (d.count / maxDay) * 80)}px`,
                      background: '#6f42c1',
                      borderRadius: '3px 3px 0 0',
                      opacity: 0.7 + (d.count / maxDay) * 0.3,
                      cursor: 'default',
                    }}
                  />
                ))}
              </div>
              <div className="d-flex justify-content-between mt-1">
                <span className="text-muted" style={{ fontSize: '11px' }}>30 days ago</span>
                <span className="text-muted" style={{ fontSize: '11px' }}>Today</span>
              </div>
            </div>
          </div>
        </div>

        {/* Plan distribution */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Plan Distribution</h6>
              {Object.entries(stats.plans).map(([plan, count]) => {
                const pct = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
                return (
                  <div key={plan} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span style={{ fontSize: '13px', color: planColor[plan], fontWeight: 600 }}>{planLabel[plan]}</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{count} ({pct}%)</span>
                    </div>
                    <div style={{ background: 'var(--header-bg)', borderRadius: '4px', height: '6px' }}>
                      <div style={{ width: `${pct}%`, background: planColor[plan], height: '6px', borderRadius: '4px', transition: 'width 0.5s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Voice profiles */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Quick Stats</h6>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: 'var(--page-bg)' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text-main)' }}>🎙️ Voice Profiles</span>
                  <Link to="/admin/voices" className="fw-bold" style={{ color: '#6f42c1', fontSize: '18px', textDecoration: 'none' }}>{stats.voiceProfiles}</Link>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: 'var(--green-section)' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text-main)' }}>📚 Total Stories</span>
                  <Link to="/admin/stories" className="fw-bold" style={{ color: '#059669', fontSize: '18px', textDecoration: 'none' }}>{stats.totalStories.toLocaleString()}</Link>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: '#fffbeb' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text-main)' }}>🎁 Total Gift Orders</span>
                  <Link to="/admin/gifts" className="fw-bold" style={{ color: '#d97706', fontSize: '18px', textDecoration: 'none' }}>{stats.giftOrdersThisMonth}</Link>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: 'var(--purple-section)' }}>
                  <span style={{ fontSize: '14px', color: 'var(--text-main)' }}>📚 Story Series</span>
                  <Link to="/admin/series" className="fw-bold" style={{ color: '#6f42c1', fontSize: '18px', textDecoration: 'none' }}>{stats.totalSeries || 0}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="row g-3 mt-1">
        {/* Recent users */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Recent Signups</h6>
                <Link to="/admin/users" style={{ fontSize: '13px', color: '#6f42c1' }}>View all</Link>
              </div>
              {recentUsers.length === 0 ? <p className="text-muted small">No users yet</p> : recentUsers.map(u => (
                <div key={u._id} className="d-flex align-items-center gap-2 mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
                    style={{ width: '32px', height: '32px', background: '#6f42c1', fontSize: '13px' }}>
                    {u.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ overflow: 'hidden', flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>{u.email}</div>
                  </div>
                  <span className="badge rounded-pill" style={{ background: planColor[u.subscription?.plan] + '22', color: planColor[u.subscription?.plan], fontSize: '11px' }}>
                    {planLabel[u.subscription?.plan]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Open support */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Open Support</h6>
                <Link to="/admin/support" style={{ fontSize: '13px', color: '#6f42c1' }}>View all</Link>
              </div>
              {recentMessages.length === 0 ? <p className="text-muted small">No open tickets</p> : recentMessages.map(m => (
                <Link key={m._id} to={`/admin/support`} style={{ textDecoration: 'none' }}>
                  <div className="mb-3 p-2 rounded-3" style={{ background: 'var(--header-bg)' }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)' }}>{m.name}</div>
                      <span className={`badge bg-${statusColor[m.status]} bg-opacity-10 text-${statusColor[m.status]}`} style={{ fontSize: '10px' }}>
                        {m.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{m.subject}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Recent gifts */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Recent Gift Orders</h6>
                <Link to="/admin/gifts" style={{ fontSize: '13px', color: '#6f42c1' }}>View all</Link>
              </div>
              {recentGifts.length === 0 ? <p className="text-muted small">No gift orders yet</p> : recentGifts.map(g => (
                <div key={g._id} className="mb-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)' }}>{g.senderName}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>→ {g.recipientEmail}</div>
                    </div>
                    <span className={`badge bg-${statusColor[g.status]} bg-opacity-10 text-${statusColor[g.status]}`} style={{ fontSize: '10px' }}>
                      {g.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
